import React from "react";
import { useBreadcrumbs } from "contexts/BreadcrumbsContext";
import {
    updateRNSDropdowns,
    fetchRNSData,
    fetchRNSCustomerData,
    downloadRNS,
    type RNSLevel,
    type RNSRow,
    type RNSCustomerRow,
} from "./api/index";
import RNSTable from "./components/RNSTable";
import RNSCustomerTable from "./components/RNSCustomerTable";
import RNSDropdowns from "./components/RNSDropdowns";

const RNSReportPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();

    // Dynamic Lineage Levels State
    const [levels, setLevels] = React.useState<RNSLevel[]>([]);
    const [dropdownLoading, setDropdownLoading] = React.useState<boolean>(true);
    const [dropdownError, setDropdownError] = React.useState<string>("");

    // 1st Level: Search and Table State
    const [search, setSearch] = React.useState<string>("");
    const [debouncedSearch, setDebouncedSearch] = React.useState<string>("");
    const [tableRows, setTableRows] = React.useState<RNSRow[]>([]);
    const [tableLoading, setTableLoading] = React.useState<boolean>(false);
    const [tableError, setTableError] = React.useState<string>("");
    const [hasLoadedOnce, setHasLoadedOnce] = React.useState<boolean>(false);

    // 1st Level Pagination and Sorting
    const [page, setPage] = React.useState<number>(1);
    const [pageSize, setPageSize] = React.useState<number>(20);
    const [totalItems, setTotalItems] = React.useState<number>(0);
    const [sortBy, setSortBy] = React.useState<keyof RNSRow>("customer_name");
    const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");

    // 2nd Level Customer Detail View State
    const [selectedCustomerCode, setSelectedCustomerCode] = React.useState<string | null>(null);
    const [selectedCustomerName, setSelectedCustomerName] = React.useState<string | null>(null);
    const [custTableRows, setCustTableRows] = React.useState<RNSCustomerRow[]>([]);
    const [custTableLoading, setCustTableLoading] = React.useState<boolean>(false);
    const [custTableError, setCustTableError] = React.useState<string>("");
    const [custHasLoadedOnce, setCustHasLoadedOnce] = React.useState<boolean>(false);

    // 2nd Level Pagination and Sorting
    const [custPage, setCustPage] = React.useState<number>(1);
    const [custPageSize, setCustPageSize] = React.useState<number>(20);
    const [custTotalItems, setCustTotalItems] = React.useState<number>(0);
    const [custSortBy, setCustSortBy] = React.useState<keyof RNSCustomerRow>("cn_number");
    const [custSortOrder, setCustSortOrder] = React.useState<"asc" | "desc">("asc");

    const [downloadingExcel, setDownloadingExcel] = React.useState<boolean>(false);

    // Active filters builder helper
    const buildFilters = React.useCallback((currentLevels: RNSLevel[]) => {
        const filters: Record<string, string[]> = {};
        currentLevels.forEach((lvl) => {
            if (lvl.selected && lvl.selected.length > 0) {
                filters[String(lvl.id)] = lvl.selected;
            }
        });
        return filters;
    }, []);

    const selectedFilters = React.useMemo(() => buildFilters(levels), [buildFilters, levels]);

    const [activeFilters, setActiveFilters] = React.useState<Record<string, string[]>>({});

    // Sync activeFilters only when the actual filter content changes
    React.useEffect(() => {
        const currentStr = JSON.stringify(selectedFilters);
        const activeStr = JSON.stringify(activeFilters);
        if (currentStr !== activeStr) {
            setActiveFilters(selectedFilters);
        }
    }, [selectedFilters, activeFilters]);

    // Load Cascading Dropdowns
    const fetchDropdowns = React.useCallback(async (selection: Record<string, string[]> = {}) => {
        setDropdownLoading(true);
        setDropdownError("");
        try {
            const resp = await updateRNSDropdowns({ selection });
            setLevels(Array.isArray(resp?.levels) ? resp.levels : []);
        } catch (err: any) {
            setDropdownError(err.message || "Failed to load filters");
            setLevels([]);
        } finally {
            setDropdownLoading(false);
        }
    }, []);

    // Set Breadcrumbs and Fetch Initial Dropdowns on Mount
    React.useEffect(() => {
        setBreadcrumbs([
            { label: "Reports", href: "/reports/pnl" },
            { label: "RNS Ageing" },
        ]);
        fetchDropdowns({});
    }, [setBreadcrumbs, fetchDropdowns]);

    // Search debounce for 1st level
    React.useEffect(() => {
        if (search === debouncedSearch) return;
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 450);
        return () => clearTimeout(timer);
    }, [search, debouncedSearch]);

    // 1st Level data loading
    const loadData = React.useCallback(async () => {
        if (selectedCustomerCode) return; // Skip if in 2nd level view
        setTableLoading(true);
        setTableError("");
        try {
            const res = await fetchRNSData({
                filters: activeFilters,
                search: debouncedSearch.trim() || undefined,
                page,
                per_page: pageSize,
                sort_by: sortBy,
                sort_order: sortOrder,
            });
            setTableRows(res.rows || []);
            setTotalItems(res.total_items || 0);
            setHasLoadedOnce(true);
        } catch (err: any) {
            setTableError(err.message || "Failed to load report data");
            setTableRows([]);
            setTotalItems(0);
        } finally {
            setTableLoading(false);
        }
    }, [activeFilters, debouncedSearch, page, pageSize, sortBy, sortOrder, selectedCustomerCode]);

    React.useEffect(() => {
        loadData();
    }, [loadData]);

    // 2nd Level Customer Details data loading
    const loadCustomerData = React.useCallback(async () => {
        if (!selectedCustomerCode) return;
        setCustTableLoading(true);
        setCustTableError("");
        try {
            const res = await fetchRNSCustomerData({
                customer_code: selectedCustomerCode,
                filters: activeFilters,
                page: custPage,
                per_page: custPageSize,
                sort_by: custSortBy,
                sort_order: custSortOrder,
            });
            setCustTableRows(res.rows || []);
            setCustTotalItems(res.total_items || 0);
            setCustHasLoadedOnce(true);
        } catch (err: any) {
            setCustTableError(err.message || "Failed to load customer CN data");
            setCustTableRows([]);
            setCustTotalItems(0);
        } finally {
            setCustTableLoading(false);
        }
    }, [selectedCustomerCode, activeFilters, custPage, custPageSize, custSortBy, custSortOrder]);

    React.useEffect(() => {
        if (selectedCustomerCode) {
            loadCustomerData();
        }
    }, [loadCustomerData, selectedCustomerCode]);

    // Dropdowns selection changes cascading logic
    const handleSelectChange = React.useCallback(
        (levelId: number | string, value: string[]) => {
            const desc = (levels || [])
                .filter((l) => Boolean(l.visible))
                .sort((a, b) => Number(b.id) - Number(a.id));

            const indexById = Object.fromEntries(
                desc.map((l, i) => [String(l.id), i])
            );
            const changedIdx = indexById[String(levelId)];

            const currentLevels = levels.map((level) => {
                const idx = indexById[String(level.id)];
                if (idx === changedIdx) {
                    return {
                        ...level,
                        selected: value.length ? value : null,
                    };
                }
                if (idx > changedIdx) {
                    return {
                        ...level,
                        selected: null,
                    };
                }
                return level;
            });

            setLevels(currentLevels);
            const selection = buildFilters(currentLevels);
            fetchDropdowns(selection);
            setPage(1);
            setCustPage(1);
        },
        [levels, buildFilters, fetchDropdowns]
    );

    const toggleSort = React.useCallback((column: keyof RNSRow) => {
        const nextOrder = sortBy === column && sortOrder === "asc" ? "desc" : "asc";
        setSortBy(column);
        setSortOrder(nextOrder);
        setPage(1);
    }, [sortBy, sortOrder]);

    const toggleCustSort = React.useCallback((column: keyof RNSCustomerRow) => {
        const nextOrder = custSortBy === column && custSortOrder === "asc" ? "desc" : "asc";
        setCustSortBy(column);
        setCustSortOrder(nextOrder);
        setCustPage(1);
    }, [custSortBy, custSortOrder]);

    const handleDownloadExcel = React.useCallback(async () => {
        try {
            setDownloadingExcel(true);
            setTableError("");
            const blob = await downloadRNS({
                filters: activeFilters,
                search: debouncedSearch.trim() || undefined,
                sort_by: sortBy,
                sort_order: sortOrder,
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `rns_report.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            setTableError(err.message || "Failed to download Excel report");
        } finally {
            setDownloadingExcel(false);
        }
    }, [selectedFilters, debouncedSearch, sortBy, sortOrder]);

    // Handle Click on customer row to transition to 2nd level view
    const handleCustomerClick = React.useCallback((customerCode: string, customerName: string) => {
        setSelectedCustomerCode(customerCode);
        setSelectedCustomerName(customerName);
        setCustPage(1);
        setCustPageSize(20);
        setCustSortBy("cn_number");
        setCustSortOrder("asc");
        setCustTableRows([]);
        setCustHasLoadedOnce(false);
    }, []);

    // Handle back navigation to 1st level view
    const handleBackToSummary = React.useCallback(() => {
        setSelectedCustomerCode(null);
        setSelectedCustomerName(null);
    }, []);

    return (
        <div className="mx-auto flex h-full min-h-0 w-full max-w-screen-2xl flex-col gap-6 px-4 sm:px-6 2xl:max-w-[2000px] py-4">
            {!selectedCustomerCode ? (
                <>
                    {/* 1st Level View: Cascading Dropdowns Filter and Search controls */}
                    <div className="shrink-0 space-y-4">
                        <RNSDropdowns
                            levels={levels}
                            loading={dropdownLoading}
                            onSelectChange={handleSelectChange}
                            search={search}
                            onSearchChange={setSearch}
                            onDownloadExcel={handleDownloadExcel}
                            downloadingExcel={downloadingExcel}
                        />

                        {dropdownError && (
                            <div className="rounded-md border border-red-200 bg-red-50 p-3">
                                <p className="text-sm text-red-600">{dropdownError}</p>
                            </div>
                        )}
                    </div>

                    {tableError && (
                        <div className="shrink-0 rounded-md border border-red-200 bg-red-50 p-3">
                            <p className="text-sm text-red-600">{tableError}</p>
                        </div>
                    )}

                    {/* Customer Summary Table */}
                    <div className="min-h-0 flex-1">
                        <RNSTable
                            data={tableRows}
                            loading={tableLoading}
                            page={page}
                            pageSize={pageSize}
                            totalItems={totalItems}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSortChange={toggleSort}
                            onPageChange={(p) => setPage(p)}
                            onPageSizeChange={(size) => {
                                setPageSize(size);
                                setPage(1);
                            }}
                            onRowClick={handleCustomerClick}
                            hasLoadedOnce={hasLoadedOnce}
                        />
                    </div>
                </>
            ) : (
                /* 2nd Level View: Customer Detail Table Component (Self-Contained) */
                <div className="min-h-0 flex-1">
                    <RNSCustomerTable
                        customerName={selectedCustomerName || ""}
                        onBack={handleBackToSummary}
                        error={custTableError}
                        data={custTableRows}
                        loading={custTableLoading}
                        page={custPage}
                        pageSize={custPageSize}
                        totalItems={custTotalItems}
                        sortBy={custSortBy}
                        sortOrder={custSortOrder}
                        onSortChange={toggleCustSort}
                        onPageChange={(p) => setCustPage(p)}
                        onPageSizeChange={(size) => {
                            setCustPageSize(size);
                            setCustPage(1);
                        }}
                        hasLoadedOnce={custHasLoadedOnce}
                    />
                </div>
            )}
        </div>
    );
};

export default RNSReportPage;
